import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { products } from '@/mocks/products';
import ProductCard from '@/components/base/ProductCard';

const giftingProducts = products.filter(p => p.isGifting);

const diwaliSet = giftingProducts.find(p => p.id === 'diwali-diya-bandana-set');
const starterSet = giftingProducts.find(p => p.id === 'new-pet-parent-starter-set');
const singles = giftingProducts.filter(p => !p.id.includes('set') && p.id !== 'diwali-diya-bandana-set' && p.id !== 'new-pet-parent-starter-set');

const giftGuideRecipients = [
  {
    title: 'For the New Pet Parent',
    tagline: 'Everything they didn\'t know they needed',
    icon: 'ri-user-heart-line',
    productIds: ['new-pet-parent-starter-set', 'forest-bite-bone', 'coral-coast-leash'],
    imageUrl: 'https://readdy.ai/api/search-image?query=Beautifully%20wrapped%20gift%20box%20for%20new%20puppy%20owner%20on%20sand%20cream%20backdrop%2C%20premium%20product%20photography%2C%20warm%20lighting%2C%20luxury%20unboxing%20feel%2C%20soft%20neutral%20tones%2C%20elegant%20composition&width=600&height=400&seq=gift-guide-new-parent&orientation=landscape',
  },
  {
    title: 'For the Festive Celebrator',
    tagline: 'Diwali, Christmas, or just because',
    icon: 'ri-magic-line',
    productIds: ['diwali-diya-bandana-set', 'festive-paw-bandana', 'bounce-fetch-ball'],
    imageUrl: 'https://readdy.ai/api/search-image?query=Festive%20Diwali%20gift%20box%20for%20pets%20with%20marigold%20flowers%20and%20warm%20golden%20decorations%20on%20sand%20cream%20backdrop%2C%20premium%20product%20photography%2C%20elegant%20festive%20mood%2C%20soft%20lighting%2C%20luxurious%20feel&width=600&height=400&seq=gift-guide-festive&orientation=landscape',
  },
  {
    title: 'For the Eco-Conscious Pet Lover',
    tagline: 'Sustainable gifts that feel as good as they look',
    icon: 'ri-leaf-line',
    productIds: ['jute-twist-dental', 'hemp-trail-collar', 'coco-stick-light-chew'],
    imageUrl: 'https://readdy.ai/api/search-image?query=Eco-friendly%20sustainable%20dog%20toys%20and%20accessories%20arranged%20in%20beautiful%20gift%20composition%20on%20sand%20cream%20backdrop%2C%20natural%20materials%2C%20organic%20textures%2C%20premium%20product%20photography%2C%20warm%20lighting&width=600&height=400&seq=gift-guide-eco&orientation=landscape',
  },
];

const howGiftingWorks = [
  {
    step: '01',
    title: 'Pick Your Gift',
    description: 'Browse our curated edits or build your own bundle. Every item is crafted from non-toxic, biodegradable materials you can feel good about giving.',
    icon: 'ri-gift-line',
  },
  {
    step: '02',
    title: 'We Pack It Beautifully',
    description: 'Every gift comes in our reusable, gift-ready packaging. No plastic wrap, no wasteful filler — just beautiful, sustainable presentation that wows from the moment it arrives.',
    icon: 'ri-box-3-line',
  },
  {
    step: '03',
    title: 'Delivered to Their Door',
    description: 'We coordinate delivery details over WhatsApp — so you can include a personal message or surprise delivery instructions. Pan-India shipping, 5-7 business days.',
    icon: 'ri-truck-line',
  },
];

export default function Gifting() {
  const guideProducts = useMemo(() => {
    return giftGuideRecipients.map(recipient => ({
      ...recipient,
      items: recipient.productIds
        .map(id => products.find(p => p.id === id))
        .filter(Boolean) as typeof products,
    }));
  }, []);

  return (
    <div className="bg-background-50 min-h-screen">
      {/* Hero Section */}
      <section className="relative w-full h-[500px] md:h-[580px] overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url("https://readdy.ai/api/search-image?query=Beautifully%20arranged%20eco-friendly%20pet%20toys%20and%20accessories%20gift%20boxes%20with%20warm%20festive%20lighting%2C%20marigold%20flowers%2C%20natural%20cotton%20ribbon%20wrapping%2C%20on%20a%20sand%20cream%20surface%2C%20premium%20editorial%20product%20photography%2C%20warm%20golden%20tones%2C%20soft%20natural%20light%2C%20elegant%20composition%2C%20gifting%20aesthetic%2C%20Indian%20festive%20mood%2C%20luxurious%20feel&width=1800&height=1160&seq=gifting-hero-2026&orientation=landscape")`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/25 to-black/45" />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center w-full px-4 md:px-8 max-w-3xl mx-auto">
            <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center mx-auto mb-5 bg-background-50/15 backdrop-blur-sm rounded-full">
              <i className="ri-gift-line text-3xl md:text-4xl text-background-50"></i>
            </div>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-background-50 mb-4 leading-tight">
              Gifting Edits
            </h1>
            <p className="text-base md:text-lg text-background-50/85 max-w-xl mx-auto leading-relaxed">
              Thoughtfully curated gift sets for the pet parents in your life — packed with love, wrapped in sustainability, and delivered with a personal touch.
            </p>
            <div className="flex items-center justify-center gap-3 mt-7 flex-wrap">
              <a
                href="#featured-sets"
                className="px-6 py-3 bg-accent-500 text-background-50 rounded-full font-semibold text-sm hover:bg-accent-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                Explore Edits
              </a>
              <a
                href="https://wa.me/91XXXXXXXXXX?text=Hi%20PounceBounce!%20I%27d%20like%20to%20know%20more%20about%20your%20gifting%20options"
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="px-6 py-3 bg-background-50/15 backdrop-blur-sm text-background-50 border border-background-50/30 rounded-full font-semibold text-sm hover:bg-background-50/25 transition-colors duration-200 whitespace-nowrap cursor-pointer flex items-center gap-2"
              >
                <i className="ri-whatsapp-line"></i>
                Chat About Gifting
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Gift Guide Recipients */}
      <section className="py-14 md:py-20 px-4 md:px-8 lg:px-12">
        <div className="max-w-screen-xl mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <div className="w-10 h-10 flex items-center justify-center mx-auto mb-4 bg-secondary-100 rounded-full">
              <i className="ri-compasses-2-line text-lg text-foreground-600"></i>
            </div>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
              Find the Perfect Gift
            </h2>
            <p className="text-sm md:text-base text-foreground-500 max-w-lg mx-auto">
              Three curated paths — because every pet parent deserves something that feels hand-picked just for them.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {guideProducts.map((recipient) => (
              <div
                key={recipient.title}
                className="bg-secondary-100 rounded-2xl overflow-hidden group transition-all duration-300 hover:shadow-md"
              >
                <div className="relative h-52 md:h-56 overflow-hidden">
                  <img
                    src={recipient.imageUrl}
                    alt={recipient.title}
                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 flex items-center justify-center bg-background-50/90 backdrop-blur-sm rounded-full">
                        <i className={`${recipient.icon} text-sm text-accent-500`}></i>
                      </div>
                      <div>
                        <h3 className="font-heading text-base font-bold text-background-50">{recipient.title}</h3>
                        <p className="text-xs text-background-50/75">{recipient.tagline}</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="p-5">
                  <ul className="space-y-2 mb-4">
                    {recipient.items.map(item => (
                      <li key={item.id} className="flex items-center gap-2.5">
                        <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                          <i className="ri-footprint-fill text-[10px] text-accent-500"></i>
                        </div>
                        <Link
                          to={`/product/${item.slug}`}
                          className="text-sm text-foreground-700 hover:text-accent-500 transition-colors duration-150 truncate cursor-pointer"
                        >
                          {item.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to={recipient.items[0] ? `/product/${recipient.items[0].slug}` : '#'}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-accent-500 hover:text-accent-600 transition-colors duration-150 cursor-pointer whitespace-nowrap"
                  >
                    View gift edit
                    <i className="ri-arrow-right-line text-[10px]"></i>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Sets */}
      <section id="featured-sets" className="py-14 md:py-20 px-4 md:px-8 lg:px-12 bg-secondary-50">
        <div className="max-w-screen-xl mx-auto">
          <div className="text-center mb-10 md:mb-14">
            <div className="w-10 h-10 flex items-center justify-center mx-auto mb-4 bg-accent-100 rounded-full">
              <i className="ri-star-line text-lg text-accent-500"></i>
            </div>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
              Our Signature Gift Sets
            </h2>
            <p className="text-sm md:text-base text-foreground-500 max-w-lg mx-auto">
              Ready-to-gift bundles that make an impression — curated with our bestselling essentials and packed in reusable gift boxes.
            </p>
          </div>

          {/* Diwali Diya Bandana Set */}
          {diwaliSet && (
            <div className="mb-10 md:mb-14">
              <div className="bg-background-50 rounded-3xl overflow-hidden flex flex-col lg:flex-row">
                <div className="lg:w-1/2">
                  <div className="aspect-square overflow-hidden">
                    <img
                      src={diwaliSet.image}
                      alt={diwaliSet.name}
                      className="w-full h-full object-cover object-top"
                      loading="lazy"
                    />
                  </div>
                </div>
                <div className="lg:w-1/2 p-8 md:p-10 lg:p-12 flex flex-col justify-center">
                  <span className="inline-block px-3 py-1 bg-[#D9A441]/10 text-[#D9A441] text-[11px] font-bold uppercase tracking-widest rounded-full mb-4 whitespace-nowrap">
                    Limited Festive Edition
                  </span>
                  <h3 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                    {diwaliSet.name}
                  </h3>
                  <p className="text-sm md:text-base text-foreground-500 mb-4 leading-relaxed">
                    {diwaliSet.description}
                  </p>
                  <ul className="space-y-2 mb-6">
                    <li className="flex items-center gap-2.5 text-sm text-foreground-600">
                      <span className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                        <i className="ri-shirt-line text-accent-500 text-sm"></i>
                      </span>
                      Festive Paw Bandana — hand-block-printed with festive motifs
                    </li>
                    <li className="flex items-center gap-2.5 text-sm text-foreground-600">
                      <span className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                        <i className="ri-heart-pulse-line text-accent-500 text-sm"></i>
                      </span>
                      Limited-edition Coco Stick in festive mustard tone
                    </li>
                    <li className="flex items-center gap-2.5 text-sm text-foreground-600">
                      <span className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                        <i className="ri-gift-2-line text-accent-500 text-sm"></i>
                      </span>
                      Diwali-ready gift packaging with traditional touches
                    </li>
                  </ul>
                  <div className="flex items-center gap-3 mb-6">
                    <span className="text-2xl font-bold text-foreground-900">Rs.{diwaliSet.price}</span>
                    {diwaliSet.mrp && (
                      <span className="text-sm text-foreground-400 line-through">Rs.{diwaliSet.mrp}</span>
                    )}
                    {diwaliSet.mrp && (
                      <span className="px-2.5 py-0.5 bg-accent-100 text-accent-600 text-[11px] font-bold rounded-full whitespace-nowrap">
                        SAVE Rs.{diwaliSet.mrp - diwaliSet.price}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mb-2">
                    {[...Array(5)].map((_, i) => (
                      <i
                        key={i}
                        className={`text-sm ${i < Math.floor(diwaliSet.rating) ? 'ri-star-fill text-[#D9A441]' : 'ri-star-line text-foreground-300'}`}
                      ></i>
                    ))}
                    <span className="text-xs text-foreground-400 ml-1">({diwaliSet.reviewCount} reviews)</span>
                  </div>
                  <div className="flex items-center gap-3 mt-6 flex-wrap">
                    <Link
                      to={`/product/${diwaliSet.slug}`}
                      className="px-6 py-3 bg-accent-500 text-background-50 rounded-full font-semibold text-sm hover:bg-accent-600 transition-colors duration-200 cursor-pointer whitespace-nowrap"
                    >
                      View Gift Details
                    </Link>
                    <Link
                      to={`/product/${diwaliSet.slug}`}
                      className="px-6 py-3 bg-secondary-100 text-foreground-700 rounded-full font-semibold text-sm hover:bg-secondary-200 transition-colors duration-200 cursor-pointer whitespace-nowrap flex items-center gap-2"
                    >
                      <i className="ri-shopping-bag-line"></i>
                      Add to Cart
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* New Pet Parent Starter Set */}
          {starterSet && (
            <div>
              <div className="bg-background-50 rounded-3xl overflow-hidden flex flex-col lg:flex-row-reverse">
                <div className="lg:w-1/2">
                  <div className="aspect-square overflow-hidden">
                    <img
                      src={starterSet.image}
                      alt={starterSet.name}
                      className="w-full h-full object-cover object-top"
                      loading="lazy"
                    />
                  </div>
                </div>
                <div className="lg:w-1/2 p-8 md:p-10 lg:p-12 flex flex-col justify-center">
                  <span className="inline-block px-3 py-1 bg-primary-100 text-primary-600 text-[11px] font-bold uppercase tracking-widest rounded-full mb-4 whitespace-nowrap">
                    Most Gifted
                  </span>
                  <h3 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                    {starterSet.name}
                  </h3>
                  <p className="text-sm md:text-base text-foreground-500 mb-4 leading-relaxed">
                    {starterSet.description}
                  </p>
                  <ul className="space-y-2 mb-6">
                    <li className="flex items-center gap-2.5 text-sm text-foreground-600">
                      <span className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                        <i className="ri-leaf-line text-primary-500 text-sm"></i>
                      </span>
                      Forest Bite Rubber Chew Bone — for healthy chewing
                    </li>
                    <li className="flex items-center gap-2.5 text-sm text-foreground-600">
                      <span className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                        <i className="ri-footprint-line text-primary-500 text-sm"></i>
                      </span>
                      Bounce Fetch Ball — unpredictable, engaging play
                    </li>
                    <li className="flex items-center gap-2.5 text-sm text-foreground-600">
                      <span className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                        <i className="ri-shirt-line text-primary-500 text-sm"></i>
                      </span>
                      Hemp Trail Collar + Coral Coast Leash — walks, sorted
                    </li>
                    <li className="flex items-center gap-2.5 text-sm text-foreground-600">
                      <span className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                        <i className="ri-box-3-line text-primary-500 text-sm"></i>
                      </span>
                      Reusable gift box that doubles as toy storage
                    </li>
                  </ul>
                  <div className="flex items-center gap-3 mb-6">
                    <span className="text-2xl font-bold text-foreground-900">Rs.{starterSet.price}</span>
                    {starterSet.mrp && (
                      <span className="text-sm text-foreground-400 line-through">Rs.{starterSet.mrp}</span>
                    )}
                    {starterSet.mrp && (
                      <span className="px-2.5 py-0.5 bg-accent-100 text-accent-600 text-[11px] font-bold rounded-full whitespace-nowrap">
                        SAVE Rs.{starterSet.mrp - starterSet.price}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mb-2">
                    {[...Array(5)].map((_, i) => (
                      <i
                        key={i}
                        className={`text-sm ${i < Math.floor(starterSet.rating) ? 'ri-star-fill text-[#D9A441]' : 'ri-star-line text-foreground-300'}`}
                      ></i>
                    ))}
                    <span className="text-xs text-foreground-400 ml-1">({starterSet.reviewCount} reviews)</span>
                  </div>
                  <div className="flex items-center gap-3 mt-6 flex-wrap">
                    <Link
                      to={`/product/${starterSet.slug}`}
                      className="px-6 py-3 bg-accent-500 text-background-50 rounded-full font-semibold text-sm hover:bg-accent-600 transition-colors duration-200 cursor-pointer whitespace-nowrap"
                    >
                      View Gift Details
                    </Link>
                    <Link
                      to={`/product/${starterSet.slug}`}
                      className="px-6 py-3 bg-secondary-100 text-foreground-700 rounded-full font-semibold text-sm hover:bg-secondary-200 transition-colors duration-200 cursor-pointer whitespace-nowrap flex items-center gap-2"
                    >
                      <i className="ri-shopping-bag-line"></i>
                      Add to Cart
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Curated Singles */}
      {singles.length > 0 && (
        <section className="py-14 md:py-20 px-4 md:px-8 lg:px-12">
          <div className="max-w-screen-xl mx-auto">
            <div className="text-center mb-10 md:mb-14">
              <div className="w-10 h-10 flex items-center justify-center mx-auto mb-4 bg-secondary-100 rounded-full">
                <i className="ri-heart-add-line text-lg text-foreground-600"></i>
              </div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                Gift-Worthy Singles
              </h2>
              <p className="text-sm md:text-base text-foreground-500 max-w-lg mx-auto">
                Standalone pieces that make perfect presents — from festive bandanas to everyday essentials that feel special.
              </p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {singles.map(product => (
                <div key={product.id} data-product-shop>
                  <ProductCard product={product} />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* How Gifting Works */}
      <section className="py-14 md:py-20 px-4 md:px-8 lg:px-12 bg-secondary-50">
        <div className="max-w-screen-xl mx-auto">
          <div className="text-center mb-10 md:mb-14">
            <div className="w-10 h-10 flex items-center justify-center mx-auto mb-4 bg-accent-100 rounded-full">
              <i className="ri-guide-line text-lg text-accent-500"></i>
            </div>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
              How Gifting Works
            </h2>
            <p className="text-sm md:text-base text-foreground-500 max-w-lg mx-auto">
              Three simple steps to the perfect pet parent gift — no stress, no plastic, just thoughtful gifting.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {howGiftingWorks.map((step, idx) => (
              <div
                key={step.step}
                className="relative bg-background-50 rounded-2xl p-7 md:p-8 text-center group hover:shadow-md transition-all duration-300"
              >
                <div className="w-14 h-14 flex items-center justify-center mx-auto mb-5 bg-accent-100 rounded-full group-hover:bg-accent-500 transition-colors duration-300">
                  <i className={`${step.icon} text-xl text-accent-500 group-hover:text-background-50 transition-colors duration-300`}></i>
                </div>
                <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-foreground-300 mb-2 block">
                  Step {step.step}
                </span>
                <h3 className="font-heading text-base font-bold text-foreground-900 mb-2">
                  {step.title}
                </h3>
                <p className="text-sm text-foreground-500 leading-relaxed">
                  {step.description}
                </p>
                {idx < howGiftingWorks.length - 1 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                    <div className="w-8 h-8 flex items-center justify-center bg-accent-100 rounded-full">
                      <i className="ri-arrow-right-line text-accent-500 text-sm"></i>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Corporate & Bulk Gifting Banner */}
      <section className="py-14 md:py-20 px-4 md:px-8 lg:px-12">
        <div className="max-w-screen-xl mx-auto">
          <div className="relative rounded-3xl overflow-hidden bg-primary-500">
            <div className="absolute inset-0 opacity-10">
              <div
                className="w-full h-full bg-cover bg-center"
                style={{
                  backgroundImage: `url("https://readdy.ai/api/search-image?query=Multiple%20eco-friendly%20gift%20boxes%20stacked%20beautifully%20with%20natural%20ribbon%20and%20dried%20flowers%2C%20corporate%20gifting%20aesthetic%2C%20warm%20golden%20lighting%2C%20sand%20cream%20backdrop%2C%20premium%20product%20photography%2C%20luxurious%20composition%2C%20elegant%20and%20professional%20feel&width=1400&height=600&seq=corporate-gifting-bg&orientation=landscape")`,
                }}
              />
            </div>
            <div className="relative p-10 md:p-14 lg:p-16 flex flex-col lg:flex-row items-center justify-between gap-8">
              <div className="flex-1 text-center lg:text-left">
                <div className="flex items-center gap-2.5 mb-4 justify-center lg:justify-start">
                  <div className="w-9 h-9 flex items-center justify-center bg-background-50/20 backdrop-blur-sm rounded-full">
                    <i className="ri-building-4-line text-background-50"></i>
                  </div>
                  <span className="text-xs font-bold uppercase tracking-[0.2em] text-background-50/60 whitespace-nowrap">
                    Corporate &amp; Bulk Gifting
                  </span>
                </div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-background-50 mb-3">
                  Gifting for Your Team, Clients &amp; Community
                </h2>
                <p className="text-sm md:text-base text-background-50/80 max-w-xl leading-relaxed">
                  Ordering for your office, event, or wedding? We offer bulk pricing, custom packaging, and personalised notes for orders of 10+ gift sets. Perfect for Diwali hampers, welcome kits, and corporate wellness initiatives.
                </p>
              </div>
              <div className="flex-shrink-0">
                <a
                  href="https://wa.me/91XXXXXXXXXX?text=Hi%20PounceBounce!%20I%27d%20like%20to%20inquire%20about%20corporate%20and%20bulk%20gifting"
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="inline-flex items-center gap-2 px-7 py-3.5 bg-background-50 text-primary-500 rounded-full font-semibold text-sm hover:bg-background-50/90 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-whatsapp-line"></i>
                  Inquire on WhatsApp
                </a>
                <p className="text-xs text-background-50/50 mt-3 text-center">
                  Min. 10 sets for bulk pricing
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-14 md:py-20 px-4 md:px-8 lg:px-12">
        <div className="max-w-screen-lg mx-auto text-center">
          <div className="w-12 h-12 flex items-center justify-center mx-auto mb-5 bg-accent-100 rounded-full">
            <i className="ri-heart-2-line text-xl text-accent-500"></i>
          </div>
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
            Not Sure What to Pick?
          </h2>
          <p className="text-sm md:text-base text-foreground-500 max-w-md mx-auto mb-8 leading-relaxed">
            Tell us about the pet parent you&apos;re shopping for — their pet type, play style, and any special occasion — and we&apos;ll personally curate the perfect gift edit for you.
          </p>
          <div className="flex items-center justify-center gap-3 flex-wrap">
            <a
              href="https://wa.me/91XXXXXXXXXX?text=Hi%20PounceBounce!%20I%27d%20love%20help%20picking%20the%20perfect%20gift.%20Can%20you%20curate%20something%20for%20me%3F"
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="inline-flex items-center gap-2 px-7 py-3.5 bg-[#25D366] text-background-50 rounded-full font-semibold text-sm hover:bg-[#1ea855] transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-whatsapp-fill text-base"></i>
              Let Us Curate for You
            </a>
            <Link
              to="/shop"
              className="inline-flex items-center gap-2 px-7 py-3.5 bg-secondary-100 text-foreground-700 rounded-full font-semibold text-sm hover:bg-secondary-200 transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-store-2-line"></i>
              Browse All Products
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}